import {
  Badge,
  LoadingIndicator,
  MUIDateTimePicker,
  MUISelect,
  MUITextField,
} from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { AxiosResponse } from "axios"
import dayjs, { Dayjs } from "dayjs"
import { StatusCodes } from "http-status-codes"
import { SetStateAction, useCallback, useContext, useEffect, useState } from "react"
import {
  Controller,
  ControllerRenderProps,
  FieldError,
  Resolver,
  useForm,
} from "react-hook-form"
import { toast } from "react-toastify"
import { useApplicationRoles } from "../../../../../hooks/useApplicationRoles"
import { useNotificationTopics } from "../../../../../hooks/useNotificationTopics"
import { digitalManagerApi } from "../../../../../services/api"
import DiMaCheckboxList from "../../../../components/DiMaCheckboxList"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import { ApplicationRoleGet } from "../../../../models/ApplicationRole"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { Guid } from "../../../../models/CustomType"
import {
  NotificationGet,
  NotificationPut,
  NotificationTarget,
} from "../../../../models/Notification"
import { NotificationTargetType } from "../../../../models/NotificationTarget"
import { getNiceFormattedDate, getUtcDateTime } from "../../../../utils/getFormattedDate"
import { getErrorMessage } from "../../../../utils/getErrorMessage"
import { getNotificationStatusProps } from "../../../../utils/getNotificationStatusProps"
import { createNotificationValidationSchema } from "../../../../components/Helpers"

type NotificationPutTextFields =
  | "title"
  | "body"
  | "url"
  | "applicationVersion"
  | "operatingSystem"

const validationSchema = createNotificationValidationSchema({
  includeTargetRoles: true,
})

const returnURL = `/notifications/`
const MAX_DATE = dayjs("9999-12-31T23:59:59")

const notificationUpdateErrorMessage = "Cannot update notification"

const getNotificationTargetType = (
  notification: NotificationGet
): NotificationTargetType => {
  if (notification.isAnonymous) {
    return NotificationTargetType.Anonymous
  }

  let targetType = NotificationTargetType.Role

  notification.targets.forEach((target: NotificationTarget) => {
    if (target.type === NotificationTargetType.All) {
      targetType = NotificationTargetType.All
    }
  })

  return targetType
}

const notificationTargetsContainRole = (
  roleId: string,
  targets: NotificationTarget[]
): boolean => {
  let contains = false
  targets.forEach((target) => {
    if (target.type === NotificationTargetType.Role && target.value === roleId) {
      contains = true
    }
  })

  return contains
}

export function NotificationDetails(props: {
  readonly notification: NotificationGet
  readonly setNotification: (updatedNotification: NotificationGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)

  const { notification, setNotification } = props

  const { notificationTopicsList } = useNotificationTopics(
    false,
    notification.application.id
  )
  const { applicationRoles, isLoadingApplicationRoles } = useApplicationRoles(
    notification.application.id
  )
  const [checkedApplicationRolesIds, setCheckedApplicationRolesIds] = useState<Guid[]>([])
  const [targetRoles, setTargetRoles] = useState<Guid[]>([])

  const formMethods = useForm<
    NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] }
  >({
    resolver: zodResolver(validationSchema) as Resolver<
      NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] }
    >,
    mode: "onChange",
  })

  const { control, setValue, getValues, handleSubmit, reset } = formMethods

  useEffect(() => {
    reset({
      title: notification.title,
      body: notification.body,
      bodyHtml: notification.bodyHtml,
      bodyPlate: notification.bodyPlate,
      url: notification.url ?? "",
      applicationVersion: notification.applicationVersion ?? "",
      operatingSystem: notification.operatingSystem ?? "",
      validityStartDate: notification.validityStartDate
        ? new Date(notification.validityStartDate).toISOString()
        : "",
      validityEndDate: notification.validityEndDate
        ? new Date(notification.validityEndDate).toISOString()
        : "",
      topicId: notification.topic.id,
      isAnonymous: notification.isAnonymous,
      targetType: getNotificationTargetType(notification),
      targetRoles,
    })
  }, [notification, targetRoles, reset])

  const updateNotificationTargets = useCallback(
    async (
      targetType: NotificationTargetType,
      values: NotificationPut & {
        targetType: NotificationTargetType
        targetRoles: Guid[]
      }
    ) => {
      const promises: Promise<AxiosResponse<NotificationTarget>>[] = []

      for (const target of notification.targets) {
        promises.push(
          digitalManagerApi.delete(
            `/notifications/${notification.id}/targets/${target.id}`
          )
        )
      }

      if (targetType === NotificationTargetType.All) {
        promises.push(
          digitalManagerApi.post<NotificationTarget>(
            `/notifications/${notification.id}/targets`,
            { type: NotificationTargetType.All }
          )
        )
      } else {
        for (const roleId of values.targetRoles) {
          promises.push(
            digitalManagerApi.post<NotificationTarget>(
              `/notifications/${notification.id}/targets`,
              { type: NotificationTargetType.Role, value: roleId }
            )
          )
        }
      }

      return Promise.all(promises)
    },
    [notification]
  )

  const onSubmit = useCallback(
    async (
      values: NotificationPut & {
        targetType: NotificationTargetType
        targetRoles: Guid[]
      }
    ) => {
      try {
        const utcValidityStartDate = values.validityStartDate
          ? getUtcDateTime(values.validityStartDate)
          : ""
        const utcValidityEndDate = values.validityEndDate
          ? getUtcDateTime(values.validityEndDate)
          : ""

        let { targetType } = values
        let isAnonymous

        if (values.targetType === NotificationTargetType.Anonymous) {
          targetType = NotificationTargetType.All
          isAnonymous = true
        } else {
          isAnonymous = false
        }

        const notificationPut: NotificationPut = {
          topicId: values.topicId,
          title: values.title,
          body: values.body,
          bodyHtml: `<p>${values.body.replaceAll("\n", "<br>")}</p>`,
          bodyPlate: values.body,
          url: values.url ? values.url : null,
          applicationVersion: values.applicationVersion
            ? values.applicationVersion
            : null,
          operatingSystem: values.operatingSystem ? values.operatingSystem : null,
          validityStartDate: utcValidityStartDate,
          validityEndDate: utcValidityEndDate,
          isAnonymous,
        }

        const res = await digitalManagerApi.put<NotificationGet>(
          `/notifications/${notification.id}`,
          notificationPut
        )

        if (res.status === StatusCodes.OK) {
          const responses = await updateNotificationTargets(targetType, values)
          const targets: NotificationTarget[] = responses
            .filter((r) => r.status === StatusCodes.CREATED)
            .map((r) => r.data)

          toast.success("Notification updated")
          setNotification({ ...res.data, targets })
        } else {
          throw new Error(notificationUpdateErrorMessage)
        }
      } catch (error) {
        toast.error(getErrorMessage(error, notificationUpdateErrorMessage))
      }
    },
    [notification, setNotification, updateNotificationTargets]
  )

  const handleValidityStartDateChange = useCallback(
    (date: Dayjs | null) => {
      setValue("validityStartDate", date ? date.toISOString() : "", {
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const handleValidityEndDateChange = useCallback(
    (date: Dayjs | null) => {
      setValue("validityEndDate", date ? date.toISOString() : "", {
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const handleChangeTargetType = useCallback(
    (value: NotificationTargetType) => {
      if (
        value === NotificationTargetType.All ||
        value === NotificationTargetType.Role ||
        value === NotificationTargetType.Anonymous
      ) {
        setValue("targetType", value, { shouldValidate: true })
        if (value !== NotificationTargetType.Role) {
          setCheckedApplicationRolesIds([])
          setValue("targetRoles", [], { shouldValidate: true })
        }
      }
    },
    [setValue]
  )

  const handleTopicSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "topicId"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const handleValidityStartDatePickerChange = useCallback(
    (date: Dayjs | null) => {
      if (date?.isAfter(MAX_DATE)) {
        setValue("validityStartDate", MAX_DATE.toISOString(), {
          shouldValidate: true,
        })
        return
      }
      handleValidityStartDateChange(date)
    },
    [handleValidityStartDateChange, setValue]
  )

  const handleValidityEndDatePickerChange = useCallback(
    (date: Dayjs | null) => {
      const startDate = getValues("validityStartDate")
        ? dayjs(getValues("validityStartDate"))
        : null

      if (date && startDate && date.isBefore(startDate)) {
        setValue("validityEndDate", "", { shouldValidate: true })
        return
      }

      if (date?.isAfter(MAX_DATE)) {
        setValue("validityEndDate", MAX_DATE.toISOString(), { shouldValidate: true })
        return
      }

      handleValidityEndDateChange(date)
    },
    [getValues, handleValidityEndDateChange, setValue]
  )

  const handleTextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        NotificationPutTextFields
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  useEffect(() => {
    setValue("targetRoles", checkedApplicationRolesIds)
  }, [checkedApplicationRolesIds, setValue])

  useEffect(() => {
    if (applicationRoles) {
      const checkedRoles = applicationRoles.filter((role: ApplicationRoleGet) =>
        notificationTargetsContainRole(role.id, notification.targets)
      )
      const checkedRolesIds = checkedRoles
        .map((checkedRole) => checkedRole.id)
        .sort((a, b) => (a ?? "").localeCompare(b ?? ""))
      setTargetRoles(checkedRolesIds)
      setCheckedApplicationRolesIds(checkedRolesIds)
    }
  }, [applicationRoles, notification.targets])

  const renderTextFieldTitle = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "title"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUITextField
        id="title"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof error?.message === "string"
            ? { title: { message: error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderTextFieldBody = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "body"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUITextField
        id="body"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        multiline
        errors={
          typeof error?.message === "string"
            ? { body: { message: error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderTextFieldUrl = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "url"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUITextField
        id="url"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof error?.message === "string"
            ? { url: { message: error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderTextFieldApplicationVersion = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "applicationVersion"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUITextField
        id="applicationVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof error?.message === "string"
            ? { applicationVersion: { message: error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderTextFieldOperatingSystem = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "operatingSystem"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUITextField
        id="operatingSystem"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof error?.message === "string"
            ? { operatingSystem: { message: error.message } }
            : undefined
        }
      />
    ),
    [handleTextFieldChange]
  )

  const renderDateTimePickerValidityStartDate = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "validityStartDate"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUIDateTimePicker
        id="validityStartDate"
        name="validityStartDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityStartDatePickerChange}
        errors={
          error ? { validityStartDate: { message: String(error.message) } } : undefined
        }
      />
    ),
    [handleValidityStartDatePickerChange]
  )

  const renderDateTimePickerValidityEndDate = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "validityEndDate"
      >
      fieldState: { error?: FieldError }
    }) => (
      <MUIDateTimePicker
        id="validityEndDate"
        name="validityEndDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityEndDatePickerChange}
        errors={
          error ? { validityEndDate: { message: String(error.message) } } : undefined
        }
      />
    ),
    [handleValidityEndDatePickerChange]
  )

  const renderSelectTopicId = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "topicId"
      >
    }) => (
      <MUISelect
        name={field.name}
        id="topicId"
        size={32}
        dropdownLOV={notificationTopicsList.map((t) => ({
          id: String(t.id),
          name: t.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleTopicSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [notificationTopicsList, handleTopicSelectChange]
  )

  const handleSelectChangeWithTargetType = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "targetType"
      >
    ) =>
      (value: NotificationTargetType) => {
        field.onChange(value)
        handleChangeTargetType(value)
      },
    [handleChangeTargetType]
  )

  const handleSetCheckedApplicationRolesIds = useCallback(
    (update: SetStateAction<Guid[]>) => {
      const newValue =
        typeof update === "function" ? update(checkedApplicationRolesIds) : update

      setCheckedApplicationRolesIds(newValue)
      setValue("targetRoles", newValue, { shouldDirty: true })
    },
    [checkedApplicationRolesIds, setCheckedApplicationRolesIds, setValue]
  )

  const handleNotificationTargetTypeSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "targetType"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        handleSelectChangeWithTargetType(field)(value as NotificationTargetType)
      },
    [handleSelectChangeWithTargetType]
  )

  const renderSelectNotificationTargetType = useCallback(
    ({
      field,
      fieldState: { error },
    }: {
      field: ControllerRenderProps<
        NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] },
        "targetType"
      >
      fieldState: { error?: FieldError }
    }) => (
      <>
        <MUISelect
          name={field.name}
          size={32}
          dropdownLOV={[
            { id: String(NotificationTargetType.All), name: "All logged users" },
            {
              id: String(NotificationTargetType.Role),
              name: "Users with specific roles",
              isDisabled: isLoadingApplicationRoles || applicationRoles.length === 0,
            },
            {
              id: String(NotificationTargetType.Anonymous),
              name: "Only NOT logged users",
            },
          ]}
          selectedItem={field.value ? [String(field.value)] : []}
          onChange={handleNotificationTargetTypeSelectChange(field)}
          onBlur={field.onBlur}
        />
        {error && (
          <MUITextField
            isDisabled
            value=""
            errors={{
              targetType: {
                message: typeof error.message === "string" ? error.message : "",
              },
            }}
          />
        )}
      </>
    ),
    [
      applicationRoles,
      handleNotificationTargetTypeSelectChange,
      isLoadingApplicationRoles,
    ]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<
    NotificationPut & { targetType: NotificationTargetType; targetRoles: Guid[] }
  > = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      notification.application.id,
      DimaFeatureShortNameEnum.NM,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Status",
        displayItem: <Badge {...getNotificationStatusProps(notification.status)} />,
      },
      {
        id: 1,
        label: "Title",
        displayItem: <span className="text-body1">{notification.title}</span>,
        editItem: (
          <Controller name="title" control={control} render={renderTextFieldTitle} />
        ),
      },
      {
        id: 2,
        label: "Body",
        displayItem: <span className="text-body1">{notification.body}</span>,
        editItem: (
          <Controller name="body" control={control} render={renderTextFieldBody} />
        ),
      },
      {
        id: 3,
        label: "Url",
        displayItem: <span className="text-body1">{notification.url}</span>,
        editItem: <Controller name="url" control={control} render={renderTextFieldUrl} />,
      },
      {
        id: 4,
        label: "Application Versions",
        displayItem: (
          <span className="text-body1">{notification.applicationVersion}</span>
        ),
        editItem: (
          <Controller
            name="applicationVersion"
            control={control}
            render={renderTextFieldApplicationVersion}
          />
        ),
      },
      {
        id: 5,
        label: "Operating Systems",
        displayItem: <span className="text-body1">{notification.operatingSystem}</span>,
        editItem: (
          <Controller
            name="operatingSystem"
            control={control}
            render={renderTextFieldOperatingSystem}
          />
        ),
      },
      {
        id: 6,
        label: "Validity Start Date",
        displayItem: (
          <span className="text-body1">
            {getNiceFormattedDate(notification.validityStartDate)}
          </span>
        ),
        editItem: (
          <Controller
            name="validityStartDate"
            control={control}
            render={renderDateTimePickerValidityStartDate}
          />
        ),
      },
      {
        id: 7,
        label: "Validity End Date",
        displayItem: (
          <span className="text-body1">
            {getNiceFormattedDate(notification.validityEndDate)}
          </span>
        ),
        editItem: (
          <Controller
            name="validityEndDate"
            control={control}
            render={renderDateTimePickerValidityEndDate}
          />
        ),
      },
      {
        id: 8,
        label: "Topic",
        displayItem: <span className="text-body1">{notification.topic.name}</span>,
        editItem: (
          <Controller name="topicId" control={control} render={renderSelectTopicId} />
        ),
      },
      {
        id: 9,
        label: "Who will receive",
        displayItem: isLoadingApplicationRoles ? (
          <LoadingIndicator variant="lazy-load" size="small" />
        ) : (
          <span className="text-body1">
            {notification.targets
              .map((target) => {
                if (target.type === NotificationTargetType.All) {
                  if (notification.isAnonymous) {
                    return "Only NOT logged users"
                  } else {
                    return "All logged users"
                  }
                } else if (target.type === NotificationTargetType.Anonymous) {
                  return "Anonymous"
                } else {
                  const role = applicationRoles.find((r) => r.id === target.value)
                  return role?.name ?? "Unknown role"
                }
              })
              .join(", ")}
          </span>
        ),
        editItem: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Controller
              name="targetType"
              control={control}
              render={renderSelectNotificationTargetType}
            />
            {getValues("targetType") === NotificationTargetType.Role && (
              <DiMaCheckboxList
                items={applicationRoles.map((role: ApplicationRoleGet) => ({
                  id: role.id,
                  title: role.name,
                  description: role.description,
                }))}
                checkedItemsIds={checkedApplicationRolesIds}
                setCheckedItemsIds={handleSetCheckedApplicationRolesIds}
                options={{ sortCheckedItemsIds: true }}
              />
            )}
          </div>
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
